import React from 'react';

// Flag partagé (avec l'écouteur vite:preloadError de main.tsx) pour ne recharger
// qu'une seule fois et éviter toute boucle de rechargement.
export const CHUNK_RELOAD_FLAG = 'chunk-reload-attempted';

/**
 * Variante de `React.lazy` tolérante aux chunks périmés après un déploiement.
 *
 * Les pages lazy sont compilées en fichiers au nom hashé (ex.
 * `ServiceManager-CrbkNe3F.js`). Un déploiement remplace tout le `dist/` :
 * les anciens fichiers hashés disparaissent du serveur. Une session ouverte
 * avant le déploiement qui navigue ensuite vers une page pas encore chargée
 * tente de récupérer l'ancien fichier → 404 → « Failed to fetch dynamically
 * imported module ».
 *
 * Ici, en cas d'échec du chargement du module, on recharge la page UNE fois
 * (récupère le nouvel index.html + les nouveaux chunks). Le flag en
 * sessionStorage empêche toute boucle : si ça échoue encore après le rechargement
 * (vraie panne réseau / fichier réellement manquant), l'erreur est propagée à
 * l'ErrorBoundary du routeur.
 */
// `any` ici reflète la contrainte de la signature de `React.lazy` lui-même
// (les composants de route acceptent des props variées / aucune).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    factory().then(
      (module) => {
        // Chargement réussi : on réarme le mécanisme pour un futur déploiement.
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
        }
        return module;
      },
      (error) => {
        const canReload =
          typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';
        if (canReload && !sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
          sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
          window.location.reload();
          // La page se recharge : promesse qui ne résout jamais, pour ne pas
          // flasher d'erreur pendant le rechargement.
          return new Promise<{ default: T }>(() => {});
        }
        throw error;
      },
    ),
  );
}
